import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../../../../../../components/ui/card";
import { Button } from "../../../../../../components/ui/button";
import { Input } from "../../../../../../components/ui/input";
import { Label } from "../../../../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../../../components/ui/table";
import { Plus, Save, FileText, Download, Upload, Trash2 } from 'lucide-react';
import { convertSetupValue, convertSetupValueToInches, getUnitFromSettings, getUnitLabel } from "../../../../../../utils/unitUtils";
import { Signature } from './Signature';
import { MultiSelect } from '../../../../../../components/ui/multi-select';
import { AntdDate } from '../../../../../../components/ui/antd-date';

/**
 * Fitment Component
 * 
 * Expected data structure for fitment specifications:
 * specifications: [
 *   {
 *     type: 'FITMENT_READINGS',
 *     value: string,
 *     label: string,
 *     toleranceSettings: {
 *       toleranceLandMax: number,
 *       toleranceLandMin: number,
 *       toleranceWallThicknessMax: number,
 *       toleranceWallThicknessMin: number
 *     }
 *   }
 * ]
 * 
 * The component automatically syncs tolerance settings when a fitment specification is selected,
 * ensuring proper data alignment and storage in the parent component.
 */

interface FitmentData {
  refLocation: number;
  landEdgePipe: number | string;
  landEdgeForging: number | string;
  landEdgeDifference: number;
  measuredLandHiLo: number | string;
  calculatedInternalHiLo: number;
  wallThicknessPipe: number | string;
  wallThicknessForging: number | string;
  calculatedExternalHiLo: number;
  bevelTopRange: number | string;
}

interface ToleranceSettings {
  toleranceLandMax: number;
  toleranceLandMin: number;
  toleranceWallThicknessMax: number;
  toleranceWallThicknessMin: number;
  toleranceInternalHiLoMin: number;
  toleranceInternalHiLoMax: number;
  toleranceExternalHiLoMin: number;
  toleranceExternalHiLoMax: number;
  toleranceBevelTopRangeMin: number;
  toleranceBevelTopRangeMax: number;
}

interface FitmentSpecification {
  value: string;
  label: string;
  toleranceSettings?: ToleranceSettings;
}

interface FitmentState {
  fitmentData: FitmentData[];
  toleranceLandMax: number;
  toleranceLandMin: number;
  toleranceWallThicknessMax: number;
  toleranceWallThicknessMin: number;
  numberOfReadings: number;
  notes: string;
  customerSignature: string | null;
  qcSignature: string | null;
}

interface FitmentProps {
  selectedItem: any;
  selectedPipe: any;
  materials: any;
  specifications: any;
  fitmentWeldData: any;
  fitmentState: any;
  onFitmentStateChange?: (newState: any) => void;
  customerRepresentativeList: Array<{ value: string, label: string }>;
  qcList: Array<{ value: string, label: string }>;
  selectedHeadings: any;
  selectedWeld: any;
  onDownloadReport?: () => void;
  downloadLoading?: boolean;
  isEditMode?: boolean;
  canEdit?: boolean;
  canReport?: boolean;
  canSign?: boolean;
  isCustomerRepresentativeUser?: boolean;
  isQCInspectorUser?: boolean;
  isWelderUser?: boolean;
  currentUserTeamMemberId?: string | number;
  isAdminOrSuperAdmin?: boolean;
}

export const Fitment: React.FC<FitmentProps> = ({
  selectedItem,
  selectedPipe,
  materials,
  specifications,
  fitmentWeldData,
  fitmentState,
  onFitmentStateChange,
  customerRepresentativeList,
  qcList,
  selectedHeadings,
  selectedWeld,
  onDownloadReport,
  downloadLoading = false,
  isEditMode = false,
  canEdit = true,
  canReport = true,
  canSign = true,
  isCustomerRepresentativeUser = false,
  isQCInspectorUser = false,
  isWelderUser = false,
  currentUserTeamMemberId,
  isAdminOrSuperAdmin = false
}) => {
  const [unit, setUnit] = useState<string>('Inches');

  // Load unit from settings
  useEffect(() => {
    const loadUnit = () => {
      const unitFromSettings = getUnitFromSettings();
      setUnit(unitFromSettings);
    };
    loadUnit();
  }, []);



  // Test conversion function for debugging


  // Use parent state if provided, otherwise use local state
  const [fitmentData, setFitmentData] = useState<FitmentData[]>(
    fitmentWeldData?.fitmentReadings || []
  );

  const [toleranceSettings, setToleranceSettings] = useState<any>(
    {
      toleranceLandMax: fitmentWeldData?.toleranceLandMax || 0.5,
      toleranceLandMin: fitmentWeldData?.toleranceLandMin || 0.75,
      toleranceWallThicknessMax: fitmentWeldData?.toleranceWallThicknessMax || 1.0,
      toleranceWallThicknessMin: fitmentWeldData?.toleranceWallThicknessMin || 2.0,
      toleranceInternalHiLoMin: fitmentWeldData?.toleranceInternalHiLoMin || 0.5,
      toleranceInternalHiLoMax: fitmentWeldData?.toleranceInternalHiLoMax || 0.75,
      toleranceExternalHiLoMin: fitmentWeldData?.toleranceExternalHiLoMin || 0.5,
      toleranceExternalHiLoMax: fitmentWeldData?.toleranceExternalHiLoMax || 0.75,
      toleranceBevelTopRangeMin: fitmentWeldData?.toleranceBevelTopRangeMin || 0.5,
      toleranceBevelTopRangeMax: fitmentWeldData?.toleranceBevelTopRangeMax || 0.75,
    }
  );

  const [nominalPipeOD, setNominalPipeOD] = useState<string>(fitmentWeldData?.details?.nominalPipeOD?.toString() || '');
  const [bevelTopRange, setBevelTopRange] = useState<string>(fitmentWeldData?.details?.bevelTopRange?.toString() || '0');  // 0-100  
  const [numberOfReadings, setNumberOfReadings] = useState<number>(fitmentWeldData?.fitmentReadings?.length || 0);
  const [notes, setNotes] = useState<string>(fitmentWeldData?.note || null);
  const [ptlName, setPtlName] = useState<string>('');
  const [ptlSignature, setPtlSignature] = useState<string>('');
  const [ptlDate, setPtlDate] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [companySignature, setCompanySignature] = useState<string>('');
  const [companyDate, setCompanyDate] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(fitmentWeldData?.date || new Date().toISOString().split('T')[0]);

  const [customerSignature, setCustomerSignature] = useState<string | null>(fitmentWeldData?.customerRepresentativeId?.toString() || null);
  const [qcSignature, setQcSignature] = useState<string | null>(fitmentWeldData?.qcInspectorId?.toString() || null);

  const [selectedFitmentId, setSelectedFitmentId] = useState<string | null>(fitmentWeldData?.details?.fitmentDocId?.toString() || null);
  const [selectedInspectionProcedureIds, setSelectedInspectionProcedureIds] = useState<string[]>(fitmentWeldData?.details?.inspectionProcedureIds || []);
  const [selectedFitment, setSelectedFitment] = useState<any>(null);
  const [isReading, setIsReading] = useState<string>(fitmentWeldData?.details?.isReading || 'accept');

  // Create options for specifications dropdowns
  const fitmentOptions = specifications
    .filter((spec: any) => spec.type === 'FITMENT_READINGS')
    .map((fitment: any) => {
      const files = fitment.files?.[0]
      return {
        value: fitment.id,
        label: files?.fileName || `Fitment ${fitment.id}`,
        numberOfReadings: files?.details?.fitmentReadings?.numberOfReadings || 0,
        updatedDate: files?.details?.fitmentReadings?.uploadDate || null,
        
        toleranceSettings: {
          toleranceLandMax: Number(files?.details?.fitmentReadings?.landMax) || '',
          toleranceLandMin: Number(files?.details?.fitmentReadings?.landMin) || '',
          toleranceWallThicknessMax: Number(files?.details?.fitmentReadings?.wallThicknessMax) || '',
          toleranceWallThicknessMin: Number(files?.details?.fitmentReadings?.wallThicknessMin) || '',
          toleranceInternalHiLoMin: Number(files?.details?.fitmentReadings?.internalHiLowMin) || '',
          toleranceInternalHiLoMax: Number(files?.details?.fitmentReadings?.internalHiLowMax) || '',
          toleranceExternalHiLoMin: Number(files?.details?.fitmentReadings?.externalHiLowMin) || '',
          toleranceExternalHiLoMax: Number(files?.details?.fitmentReadings?.externalHiLowMax) || '',
          toleranceBevelTopRangeMin: Number(files?.details?.fitmentReadings?.bevelTopRangeMin) || '',
          toleranceBevelTopRangeMax: Number(files?.details?.fitmentReadings?.bevelTopRangeMax) || '',
        }
      }
    });
  // Create options for specifications dropdowns
  const inspectionProcedureOptions = specifications
    .filter((spec: any) => spec.type === 'INSPECTION_PROCEDURE')
    .map((inspectionProcedure: any) => {
      const files= inspectionProcedure.files?.[inspectionProcedure.files.length - 1]
      return {
      value: inspectionProcedure.id,
      standardName: files?.details?.inspectionProcedure?.standard,
      label: files?.fileName || `Inspection Procedure ${inspectionProcedure.id}`
      }
    });
useEffect(() => {
  if (fitmentOptions.length > 0) {
    const selectedFitment = fitmentWeldData?.details?.fitmentDocId ? fitmentOptions.find((o: any) => String(o.value) === String(fitmentWeldData?.details?.fitmentDocId)) : fitmentOptions[0]
    setSelectedFitment(selectedFitment);
    if (selectedFitment && selectedFitment.toleranceSettings) {
      if( selectedFitment.numberOfReadings && selectedFitment.numberOfReadings > 0)
        handleNumberOfReadingsChange(selectedFitment.numberOfReadings);

      setToleranceSettings(selectedFitment.toleranceSettings);
      // if (onFitmentStateChange) {
      //   onFitmentStateChange(false);
      // }
    }
  }
}, []);

useEffect(()=>{
if(toleranceSettings && fitmentWeldData?.details)
fitmentWeldData.details.toleranceSettings =toleranceSettings
},[toleranceSettings, fitmentWeldData])



  // Sync local state with parent state when fitmentWeldData changes
  useEffect(() => {
    if (fitmentWeldData) {
      console.log(fitmentWeldData)
      // setToleranceSettings({
      //   toleranceLandMax: fitmentWeldData.toleranceLandMax || 0.5,
      //   toleranceLandMin: fitmentWeldData.toleranceLandMin || 0.75,
      //   toleranceWallThicknessMax: fitmentWeldData.toleranceWallThicknessMax || 1.0,
      //   toleranceWallThicknessMin: fitmentWeldData.toleranceWallThicknessMin || 2.0,
      // });
    //  setNumberOfReadings(fitmentWeldData.fitmentReadings?.length || 0);
      setNotes(fitmentWeldData.note || null);
      setCustomerSignature(fitmentWeldData?.customerRepresentativeId?.toString() || null);
      setQcSignature(fitmentWeldData?.qcInspectorId?.toString() || null);
      if(fitmentWeldData.fitmentReadings && fitmentWeldData.fitmentReadings.length > 0) {
        // Recalculate differences if they are 0 or empty
        const recalculatedData = fitmentWeldData.fitmentReadings.map((row: FitmentData) => {
          const safeToNumber = (val: any): number => {
            if (val === '' || val === null || val === undefined) return 0;
            const num = Number(val);
            return isNaN(num) ? 0 : num;
          };

          const pipeValue = safeToNumber(row.landEdgePipe);
          const forgingValue = safeToNumber(row.landEdgeForging);
          const measuredValue = safeToNumber(row.measuredLandHiLo);
          const wallPipeValue = safeToNumber(row.wallThicknessPipe);
          const wallForgingValue = safeToNumber(row.wallThicknessForging);

          const updatedRow = { ...row };

          // Recalculate landEdgeDifference if 0 or empty
          if (updatedRow.landEdgeDifference === 0 || updatedRow.landEdgeDifference === null || updatedRow.landEdgeDifference === undefined) {
            const difference = pipeValue - forgingValue;
            updatedRow.landEdgeDifference = Math.round(difference * 1000) / 1000;
          }

          // Recalculate calculatedInternalHiLo if 0 or empty
          if (updatedRow.calculatedInternalHiLo === 0 || updatedRow.calculatedInternalHiLo === null || updatedRow.calculatedInternalHiLo === undefined) {
            const difference = pipeValue - forgingValue;
            const sum = difference + measuredValue;
            updatedRow.calculatedInternalHiLo = Math.round(sum * 1000) / 1000;
          }

          // Recalculate calculatedExternalHiLo if 0 or empty
          if (updatedRow.calculatedExternalHiLo === 0 || updatedRow.calculatedExternalHiLo === null || updatedRow.calculatedExternalHiLo === undefined) {
            const difference = pipeValue - forgingValue;
            const sum = difference + measuredValue;
            const internalHiLo = -wallPipeValue + sum + wallForgingValue;
            updatedRow.calculatedExternalHiLo = Math.round(internalHiLo * 1000) / 1000;
          }

          return updatedRow;
        });
        setFitmentData(recalculatedData);
      } else if(selectedFitment?.numberOfReadings && selectedFitment?.numberOfReadings > 0)
        handleNumberOfReadingsChange(selectedFitment.numberOfReadings);
             // Load additional details
       if (fitmentWeldData.details) {
         setNominalPipeOD(fitmentWeldData.details.nominalPipeOD?.toString() || '');
         setBevelTopRange(fitmentWeldData.details.bevelTopRange?.toString() || '0');
         setIsReading(fitmentWeldData.details.isReading || 'accept');
         if (fitmentWeldData.details.fitmentDocId) {
           setSelectedFitmentId(fitmentWeldData.details.fitmentDocId.toString());
         }
         if (fitmentWeldData.details.inspectionProcedureIds) {
           setSelectedInspectionProcedureIds(fitmentWeldData.details.inspectionProcedureIds.map(String));
         }
       }
       if (fitmentWeldData.date) {
         setStartDate(fitmentWeldData.date);
       }
    }
  }, [fitmentWeldData]);

  // Update tolerance settings when fitment specification changes
  // This ensures that tolerance values are automatically synchronized with the selected fitment specification
  // and properly stored in the parent component's fitmentWeldData
  // useEffect(() => {
  //   if (selectedFitmentId && fitmentOptions.length > 0) {
  //     const selectedFitment = fitmentOptions.find((spec: FitmentSpecification) => spec.value === selectedFitmentId);
  //     if (selectedFitment && selectedFitment.toleranceSettings) {
  //       const newToleranceSettings = {
  //         toleranceLandMax: selectedFitment.toleranceSettings.toleranceLandMax || 0.5,
  //         toleranceLandMin: selectedFitment.toleranceSettings.toleranceLandMin || 0.75,
  //         toleranceWallThicknessMax: selectedFitment.toleranceSettings.toleranceWallThicknessMax || 1.0,
  //         toleranceWallThicknessMin: selectedFitment.toleranceSettings.toleranceWallThicknessMin || 2.0,
  //       };

  //       setToleranceSettings(newToleranceSettings);

  //       // Update fitmentWeldData with new tolerance values
  //       if (fitmentWeldData) {
  //         fitmentWeldData.toleranceLandMax = newToleranceSettings.toleranceLandMax;
  //         fitmentWeldData.toleranceLandMin = newToleranceSettings.toleranceLandMin;
  //         fitmentWeldData.toleranceWallThicknessMax = newToleranceSettings.toleranceWallThicknessMax;
  //         fitmentWeldData.toleranceWallThicknessMin = newToleranceSettings.toleranceWallThicknessMin;

  //         // Notify parent component of changes
  //         if (onFitmentStateChange) {
  //           onFitmentStateChange(true);
  //         }
  //       }
  //     }
  //   }
  // }, [fitmentOptions]);

  const handleNumberOfReadingsChange = (value: number) => {
    setNumberOfReadings(value);
    // Update table rows based on new number of readings
    const currentRowCount = fitmentData.length;
    let updatedData = [...fitmentData];

    if (value > currentRowCount) {
      // Add new rows
      const newRows: FitmentData[] = [];
      for (let i = currentRowCount + 1; i <= value; i++) {
        newRows.push({
          refLocation: i,
          landEdgePipe: '',
          landEdgeForging: '',
          landEdgeDifference: 0,
          measuredLandHiLo: '',
          calculatedInternalHiLo: 0,
          wallThicknessPipe: '',
          wallThicknessForging: '',
          calculatedExternalHiLo: 0,
          bevelTopRange: '',
        });
      }
      updatedData = [...fitmentData, ...newRows];
      setFitmentData(updatedData);

    } else if (value < currentRowCount) {
      // Remove excess rows
      updatedData = fitmentData.slice(0, value);
      setFitmentData(updatedData);
    }

    fitmentWeldData.fitmentReadings = updatedData;
    // if (onFitmentStateChange) {
    //   onFitmentStateChange(true);
    // }
  };

  useEffect(() => {
    if (fitmentWeldData) {
      fitmentWeldData.fitmentReadings = fitmentData;

      // if(selectedFitment && selectedFitment.numberOfReadings && (!fitmentWeldData.fitmentReadings || fitmentWeldData.fitmentReadings.length === 0)){
      //   handleNumberOfReadingsChange(selectedFitment.numberOfReadings);
      // }
    }
  }, [fitmentWeldData]);

  // // Check tolerance status when fitmentData or toleranceSettings change
  useEffect(() => {
    if (fitmentData.length > 0) {
      checkToleranceAndUpdateStatus();
    }
  }, [fitmentData,toleranceSettings]);

  const handleDataChange = (index: number, field: keyof FitmentData, value: number | string) => {
    const updatedData = [...fitmentData];
    
    // Store the raw value (can be empty string, negative, etc.)
    // If value is empty string, store as empty string, not 0
    updatedData[index] = { ...updatedData[index], [field]: value };

    // Helper function to safely convert to number for calculations
    const safeToNumber = (val: any): number => {
      if (val === '' || val === null || val === undefined) return 0;
      const num = Number(val);
      return isNaN(num) ? 0 : num;
    };

    // For calculations, convert to number (empty string becomes 0)
    const pipeValue = safeToNumber(updatedData[index].landEdgePipe);
    const forgingValue = safeToNumber(updatedData[index].landEdgeForging);
    const measuredValue = safeToNumber(updatedData[index].measuredLandHiLo);
    const wallPipeValue = safeToNumber(updatedData[index].wallThicknessPipe);
    const wallForgingValue = safeToNumber(updatedData[index].wallThicknessForging);

    // Calculate derived values
    const difference = pipeValue - forgingValue;
    updatedData[index].landEdgeDifference = Math.round(difference * 1000) / 1000;

    const sum = difference + measuredValue;
    updatedData[index].calculatedInternalHiLo = Math.round(sum * 1000) / 1000;

    const internalHiLo = -wallPipeValue + sum + wallForgingValue;
    updatedData[index].calculatedExternalHiLo = Math.round(internalHiLo * 1000) / 1000;

    setFitmentData(updatedData);
    fitmentWeldData.fitmentReadings = updatedData;
    
    // // Check tolerance and update isReading status
    // setTimeout(() => {
    //   checkToleranceAndUpdateStatus();
    // }, 0);
    
    if (onFitmentStateChange) {
      onFitmentStateChange(true);
    }
  };

  const addNewRow = () => {
    const newRow: FitmentData = {
      refLocation: fitmentData.length + 1,
      landEdgePipe: '',
      landEdgeForging: '',
      landEdgeDifference: 0,
      measuredLandHiLo: '',
      calculatedInternalHiLo: 0,
      wallThicknessPipe: '',
      wallThicknessForging: '',
      calculatedExternalHiLo: 0,
      bevelTopRange: '',
    };
    const updatedData = [...fitmentData, newRow];
    setFitmentData(updatedData);
    // Update number of readings to match the new row count
    const newNumberOfReadings = fitmentData.length + 1;
    setNumberOfReadings(newNumberOfReadings);
    fitmentWeldData.fitmentReadings = updatedData;
    fitmentWeldData.numberOfReadings = newNumberOfReadings;
    // Notify parent component of changes
    if (onFitmentStateChange) {
      onFitmentStateChange(true);
    }
  };

  const deleteRow = (index: number) => {
    const updatedData = fitmentData.filter((_, i) => i !== index);
    // Update refLocation numbers to be sequential
    const reorderedData = updatedData.map((row, i) => ({
      ...row,
      refLocation: i + 1
    }));
    setFitmentData(reorderedData);
    const newNumberOfReadings = reorderedData.length;
    setNumberOfReadings(newNumberOfReadings);
    fitmentWeldData.fitmentReadings = reorderedData;
    fitmentWeldData.numberOfReadings = newNumberOfReadings;
    if (onFitmentStateChange) {
      onFitmentStateChange(true);
    }
  };

  const handleToleranceSettingsChange = (field: keyof ToleranceSettings, value: number) => {
    const updatedSettings = { ...toleranceSettings, [field]: value };
    setToleranceSettings(updatedSettings);
    fitmentWeldData.toleranceLandMax = updatedSettings.toleranceLandMax;
    fitmentWeldData.toleranceLandMin = updatedSettings.toleranceLandMin;
    fitmentWeldData.toleranceWallThicknessMax = updatedSettings.toleranceWallThicknessMax;
    fitmentWeldData.toleranceWallThicknessMin = updatedSettings.toleranceWallThicknessMin;
    if (onFitmentStateChange) {
      onFitmentStateChange(true);
    }
  };

  const handleNotesChange = (value: string) => {
    setNotes(value);
    if (fitmentWeldData) {
      fitmentWeldData.note = value;
      if (onFitmentStateChange) {
        onFitmentStateChange(true);
      }
    }
  };

  const handleCustomerSignatureChange = (value: string | null) => {
    setCustomerSignature(value);
    fitmentWeldData.customerRepresentativeId = value;
    if (onFitmentStateChange) {
      onFitmentStateChange(true);
    }
  };

  const handleQcSignatureChange = (value: string | null) => {
    setQcSignature(value);
    if (fitmentWeldData) {
      fitmentWeldData.qcInspectorId = value;
      if (onFitmentStateChange) {
        onFitmentStateChange(true);
      }
    }
  };

  const OnHandleSIngatuse = (comment: { signature: string; name: string; comment?: string; stampImage?: string; type: 'customer' | 'qc' }) => {
    if (fitmentWeldData) {
      // If signature is empty, remove the signature object
      if (!comment.signature || comment.signature === '') {
        if (fitmentWeldData.signature && fitmentWeldData.signature[comment.type]) {
          delete fitmentWeldData.signature[comment.type];
        }
      } else {
        // Add or update the signature
        fitmentWeldData.signature = {
          ...fitmentWeldData.signature,
          [comment.type]: {
            signature: comment.signature,
            name: comment.name,
            comment: comment.comment,
            stampImage: comment.stampImage,
            timestamp: new Date().toISOString()
          }
        };
      }
      if (onFitmentStateChange) {
        onFitmentStateChange(true);
      }
    }
   // console.log(fitmentWeldData);
  };

  // Function to save all current data to fitmentWeldData
  const saveAllData = () => {
    // Check if fitmentWeldData is a valid object, not a boolean
    if (fitmentWeldData && typeof fitmentWeldData === 'object' && !Array.isArray(fitmentWeldData)) {
      // Ensure details object exists
      if (!fitmentWeldData.details) {
        fitmentWeldData.details = {};
      }
      
      // Save all form values
      fitmentWeldData.details.nominalPipeOD = Number(nominalPipeOD);
      fitmentWeldData.details.bevelTopRange = Number(bevelTopRange);
      fitmentWeldData.details.fitmentDocId = selectedFitmentId ? parseInt(selectedFitmentId) : null;
      fitmentWeldData.details.inspectionProcedureIds = selectedInspectionProcedureIds.map(Number);
      fitmentWeldData.details.isReading = isReading;
      fitmentWeldData.date = startDate;
      fitmentWeldData.note = notes;
      fitmentWeldData.customerRepresentativeId = customerSignature;
      fitmentWeldData.qcInspectorId = qcSignature;
      fitmentWeldData.fitmentReadings = fitmentData;
      fitmentWeldData.numberOfReadings = numberOfReadings;
      
      // Save tolerance settings
      fitmentWeldData.toleranceLandMax = toleranceSettings.toleranceLandMax;
      fitmentWeldData.toleranceLandMin = toleranceSettings.toleranceLandMin;
      fitmentWeldData.toleranceWallThicknessMax = toleranceSettings.toleranceWallThicknessMax;
      fitmentWeldData.toleranceWallThicknessMin = toleranceSettings.toleranceWallThicknessMin;
      fitmentWeldData.toleranceInternalHiLoMin = toleranceSettings.toleranceInternalHiLoMin;
      fitmentWeldData.toleranceInternalHiLoMax = toleranceSettings.toleranceInternalHiLoMax;
      fitmentWeldData.toleranceExternalHiLoMin = toleranceSettings.toleranceExternalHiLoMin;
      fitmentWeldData.toleranceExternalHiLoMax = toleranceSettings.toleranceExternalHiLoMax;
      fitmentWeldData.toleranceBevelTopRangeMin = toleranceSettings.toleranceBevelTopRangeMin;
      fitmentWeldData.toleranceBevelTopRangeMax = toleranceSettings.toleranceBevelTopRangeMax;
      
      // Call the parent callback if provided
      if (onFitmentStateChange) {
        onFitmentStateChange(fitmentWeldData);
      }
    } else {
    }
  };

  // // Save data whenever any value changes
  // useEffect(() => {
  //   saveAllData();
  // }, [nominalPipeOD, bevelTopRange, startDate, notes, customerSignature, qcSignature, selectedInspectionProcedureIds, fitmentData, numberOfReadings, toleranceSettings]);

  // Helper function to get display value for input fields (convert from inches to display unit)
  const getDisplayValue = (value: number): any => {
    if (typeof value !== 'number' || isNaN(value)) return 0;

    // If the unit is already inches, no conversion needed
    if (unit === 'in') {
      return value;
    }

    // If the unit is meters (mm), convert from inches to mm
    if (unit === 'mm') {
      const mmValue = value * 25.4;
      const roundedValue = Math.round(mmValue * 1000) / 1000;
      return roundedValue;
    }

    return value;
  };
  // Helper function to get display value for input fields (convert from inches to display unit)
  const getDisplayDifferenceValue = (value: number): number => {
    if(value <0)
      console.log(value,unit)
    if (typeof value !== 'number' || isNaN(value)) return 0;


    
    
    // If the unit is already inches, no conversion needed
    if (unit === 'in' || unit === 'Inches') {
      return value;
    }
   
    // If the unit is meters (mm), convert from inches to mm
    if (unit === 'mm') {
      const mmValue = value * 25.4;
      const roundedValue = Math.round(mmValue);
      return roundedValue;
    }

    return value;
  };

  // Helper function to convert input value back to inches for storage
  const convertInputToInches = (value: number): number => {
    if (typeof value !== 'number' || isNaN(value)) return 0;

    // If the unit is already inches, no conversion needed
    if (unit === 'in') {
      return value;
    }

    // If the unit is meters (mm), convert from mm to inches
    if (unit === 'mm') {
      const inchesValue = value / 25.4;
      const roundedValue = Math.round(inchesValue * 1000000) / 1000000;
      return roundedValue;
    }

    return value;
  };

  // Helper function to check if value is within tolerance range
  const isWithinTolerance = (value: number, min: number, max: number): boolean => {
    return value >= min && value <= max;
  };

  // Helper function to get border color based on tolerance
  const getToleranceBorderColor = (value: number, min: number, max: number, rawValue?: number | string): string => {
    // Don't show red border if the field is empty
    if (rawValue === '' || rawValue === undefined) return '';
    // Check if value is within tolerance
    return isWithinTolerance(value, min, max) ? '' : '';
  };

  // Function to check if any field is out of tolerance and update isReading status
  const checkToleranceAndUpdateStatus = () => {
    let hasOutOfTolerance = false;

    // Check all input fields for out of tolerance values
    for (const row of fitmentData) {
      // Check land edge pipe
      if (row.landEdgePipe !== '') {
        const pipeValue = getDisplayValue(typeof row.landEdgePipe === 'string' ? Number(row.landEdgePipe) : row.landEdgePipe);
        if (!isWithinTolerance(pipeValue, getDisplayValue(toleranceSettings.toleranceLandMin), getDisplayValue(toleranceSettings.toleranceLandMax))) {
          hasOutOfTolerance = true;
        //  console.log('landEdgePipe', hasOutOfTolerance,pipeValue,toleranceSettings.toleranceLandMin);
          break;
        }
      }

      // Check land edge forging
      if (row.landEdgeForging !== '') {
        const forgingValue = getDisplayValue(typeof row.landEdgeForging === 'string' ? Number(row.landEdgeForging) : row.landEdgeForging);
        if (!isWithinTolerance(forgingValue, getDisplayValue(toleranceSettings.toleranceLandMin), getDisplayValue(toleranceSettings.toleranceLandMax))) {
          hasOutOfTolerance = true;
          break;
        }
      }

      // Check measured land hi-lo
      if (row.measuredLandHiLo !== '') {
        const measuredValue = getDisplayValue(typeof row.measuredLandHiLo === 'string' ? Number(row.measuredLandHiLo) : row.measuredLandHiLo);
        if (!isWithinTolerance(measuredValue, getDisplayValue(toleranceSettings.toleranceInternalHiLoMin), getDisplayValue(toleranceSettings.toleranceInternalHiLoMax))) {
          hasOutOfTolerance = true;
          break;
        }
      }

      // Check calculated internal hi-lo - only if at least one input field that contributes to it is not empty
      // calculatedInternalHiLo = (landEdgePipe - landEdgeForging) + measuredLandHiLo
      const hasInputForInternalHiLo = row.landEdgePipe !== '' || row.landEdgeForging !== '' || row.measuredLandHiLo !== '';
      if (hasInputForInternalHiLo) {
        const calculatedInternalValue = getDisplayValue(row.calculatedInternalHiLo);
        if (!isWithinTolerance(calculatedInternalValue, getDisplayValue(toleranceSettings.toleranceInternalHiLoMin), getDisplayValue(toleranceSettings.toleranceInternalHiLoMax))) {
          hasOutOfTolerance = true;
          break;
        }
      }

      // Check wall thickness pipe
      if (row.wallThicknessPipe !== '') {
        const wallPipeValue = getDisplayValue(typeof row.wallThicknessPipe === 'string' ? Number(row.wallThicknessPipe) : row.wallThicknessPipe);
        if (!isWithinTolerance(wallPipeValue, getDisplayValue(toleranceSettings.toleranceWallThicknessMin), getDisplayValue(toleranceSettings.toleranceWallThicknessMax))) {
          hasOutOfTolerance = true;
          break;
        }
      }

      // Check wall thickness forging
      if (row.wallThicknessForging !== '') {
        const wallForgingValue = getDisplayValue(typeof row.wallThicknessForging === 'string' ? Number(row.wallThicknessForging) : row.wallThicknessForging);
        if (!isWithinTolerance(wallForgingValue, getDisplayValue(toleranceSettings.toleranceWallThicknessMin), getDisplayValue(toleranceSettings.toleranceWallThicknessMax))) {
          hasOutOfTolerance = true;
          break;
        }
      }

      // Check calculated external hi-lo - only if at least one input field that contributes to it is not empty
      // calculatedExternalHiLo = -wallThicknessPipe + (landEdgePipe - landEdgeForging) + measuredLandHiLo + wallThicknessForging
      const hasInputForExternalHiLo = row.wallThicknessPipe !== '' || row.landEdgePipe !== '' || row.landEdgeForging !== '' || row.measuredLandHiLo !== '' || row.wallThicknessForging !== '';
      if (hasInputForExternalHiLo) {
        const calculatedExternalValue = getDisplayValue(row.calculatedExternalHiLo);
        if (!isWithinTolerance(calculatedExternalValue, getDisplayValue(toleranceSettings.toleranceExternalHiLoMin), getDisplayValue(toleranceSettings.toleranceExternalHiLoMax))) {
          hasOutOfTolerance = true;
          break;
        }
      }

      // Check bevel top range
      if (row.bevelTopRange !== '') {
        const bevelValue = getDisplayValue(typeof row.bevelTopRange === 'string' ? Number(row.bevelTopRange) : row.bevelTopRange);
        if (!isWithinTolerance(bevelValue, getDisplayValue(toleranceSettings.toleranceBevelTopRangeMin), getDisplayValue(toleranceSettings.toleranceBevelTopRangeMax))) {
          hasOutOfTolerance = true;
          break;
        }
      }
    }

    // Update isReading status
    const newStatus = hasOutOfTolerance ? 'reject' : 'accept';
    setIsReading(newStatus);
    
    // Update fitmentWeldData
    if (fitmentWeldData) {
      if (!fitmentWeldData.details) {
        fitmentWeldData.details = {};
      }
      fitmentWeldData.details.isReading = newStatus;
      
      // if (onFitmentStateChange) {
      //   onFitmentStateChange(true);
      // }
    }
  };

  // Handle fitment specification change and automatically update tolerance settings
  // This ensures proper data alignment between the selected specification and the component state
    const handleFitmentChange = (value: string) => {
    setSelectedFitmentId(value);
    if (fitmentWeldData) {
      // Ensure details object exists
      if (!fitmentWeldData.details) {
        fitmentWeldData.details = {};
      }
      
      // Save fitmentDocId in details
      fitmentWeldData.details.fitmentDocId = parseInt(value);

      // Find the selected fitment specification to get tolerance values
      const selectedFitment = fitmentOptions.find((spec: FitmentSpecification) => spec.value === value);
     
      if (selectedFitment && selectedFitment.toleranceSettings) {
        const newToleranceSettings = {
          toleranceLandMax: selectedFitment.toleranceSettings.toleranceLandMax || 0.5,
          toleranceLandMin: selectedFitment.toleranceSettings.toleranceLandMin || 0.75,
          toleranceWallThicknessMax: selectedFitment.toleranceSettings.toleranceWallThicknessMax || 1.0,
          toleranceWallThicknessMin: selectedFitment.toleranceSettings.toleranceWallThicknessMin || 2.0,
          toleranceInternalHiLoMin: selectedFitment.toleranceSettings.toleranceInternalHiLoMin || 0.5,
          toleranceInternalHiLoMax: selectedFitment.toleranceSettings.toleranceInternalHiLoMax || 0.75,
          toleranceExternalHiLoMin: selectedFitment.toleranceSettings.toleranceExternalHiLoMin || 0.5,
          toleranceExternalHiLoMax: selectedFitment.toleranceSettings.toleranceExternalHiLoMax || 0.75,
          toleranceBevelTopRangeMin: selectedFitment.toleranceSettings.toleranceBevelTopRangeMin || 0.5,
          toleranceBevelTopRangeMax: selectedFitment.toleranceSettings.toleranceBevelTopRangeMax || 0.75,
          };

        setToleranceSettings(newToleranceSettings);

        // Update fitmentWeldData with new tolerance values
        fitmentWeldData.toleranceLandMax = newToleranceSettings.toleranceLandMax;
        fitmentWeldData.toleranceLandMin = newToleranceSettings.toleranceLandMin;
        fitmentWeldData.toleranceWallThicknessMax = newToleranceSettings.toleranceWallThicknessMax;
        fitmentWeldData.toleranceWallThicknessMin = newToleranceSettings.toleranceWallThicknessMin;
        fitmentWeldData.toleranceInternalHiLoMin = newToleranceSettings.toleranceInternalHiLoMin;
        fitmentWeldData.toleranceInternalHiLoMax = newToleranceSettings.toleranceInternalHiLoMax;
        fitmentWeldData.toleranceExternalHiLoMin = newToleranceSettings.toleranceExternalHiLoMin;
        fitmentWeldData.toleranceExternalHiLoMax = newToleranceSettings.toleranceExternalHiLoMax;
        fitmentWeldData.toleranceBevelTopRangeMin = newToleranceSettings.toleranceBevelTopRangeMin;
        fitmentWeldData.toleranceBevelTopRangeMax = newToleranceSettings.toleranceBevelTopRangeMax;
        }

      if (onFitmentStateChange) {
        onFitmentStateChange(true);
      }
    }
  };

  const handleInspectionProcedureChange = (value: string[]) => {
    setSelectedInspectionProcedureIds(value);
    if (fitmentWeldData) {
      // Ensure details object exists
      if (!fitmentWeldData.details) {
        fitmentWeldData.details = {};
      }
      
      // Save inspectionProcedureIds in details
      fitmentWeldData.details.inspectionProcedureIds = value.map(Number);
      if (onFitmentStateChange) {
        onFitmentStateChange(true);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Information */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-6">
            {/* Main Form Fields */}
            <div className="grid grid-cols-1 lg:grid-cols-6 gap-2">
              {/* <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">Fitment Readings</Label>
                <Select value={selectedFitmentId || ''} onValueChange={handleFitmentChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Fitment" />
                  </SelectTrigger>
                  <SelectContent>
                    {fitmentOptions.length > 0 ? (
                      fitmentOptions.map((fitment: FitmentSpecification) => (
                        <SelectItem key={fitment.value} value={fitment.value}>
                          {fitment.label}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-fitment" disabled>
                        No Fitment specifications found
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div> */}
              {/* Inspection Procedure - Full Width */}
             
              <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Date</Label>
                    <AntdDate
                      name="startDate"
                      value={startDate}
                      onChange={(value: string) => {
                        setStartDate(value);
                        if (fitmentWeldData) {
                          fitmentWeldData.date = value;
                          if (onFitmentStateChange) {
                            onFitmentStateChange(true);
                          }
                        }
                      }}
                      disabled={!isEditMode}
                    />
                  </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600">
                  Nominal Pipe O/D Diameter ({getUnitLabel(unit)})
                </Label>
                <Input
                  value={nominalPipeOD || ''}
                  onChange={(e) => {
                    setNominalPipeOD(e.target.value);
                    if (fitmentWeldData) {
                      fitmentWeldData.details.nominalPipeOD = Number(e.target.value);
                      if (onFitmentStateChange) {
                        onFitmentStateChange(true);
                      }
                    }
                  }}
                  placeholder="e.g., All Project Diameters"
                  className="w-full"
                  disabled={!isEditMode}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600">
                  Calculated Bevel Top ({getUnitLabel(unit)})
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={getDisplayValue(Number(bevelTopRange))}
                  onChange={(e) => {
                    setBevelTopRange(e.target.value);
                    if (fitmentWeldData) {
                      fitmentWeldData.details.bevelTopRange = Number(e.target.value);
                      if (onFitmentStateChange) {
                        onFitmentStateChange(true);
                      }
                    }
                  }}
                  placeholder="4"
                  className="w-full"
                  disabled={!isEditMode}
                />
              </div>

              {/* <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600">No of Readings</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={numberOfReadings}
                  onChange={(e) => handleNumberOfReadingsChange(parseInt(e.target.value) || 0)}
                  placeholder="4"
                  className="w-full"
                />
              </div>
              */}
              <div className="space-y-2 col-span-3">
                <Label className="text-sm font-medium text-gray-700">Inspection Procedure</Label>
                <div className="w-full">
                  <MultiSelect
                    options={inspectionProcedureOptions}
                    selected={selectedInspectionProcedureIds}
                    onChange={isEditMode ? handleInspectionProcedureChange : () => {}}
                  />
                </div>
              </div>
            </div>


            {/* Tolerance Settings */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-gray-700 text-lg flex items-center">
                    Tolerance Settings
                  </h4>
                  <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">Reading Status:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      isReading === 'accept' 
                        ? 'text-galvanizing-green bg-[rgba(31,154,58,0.08)] border' 
                        : 'text-readable-red bg-[rgba(196,0,31,0.06)] border'
                    }`}>
                      {isReading === 'accept' ? '✓ Accept' : '✗ Reject'}
                    </span>
                    </div>
                    {/* {onDownloadReport && fitmentState?.signature?.qc?.signature && fitmentState?.signature?.customer?.signature && (
                      <Button
                        onClick={onDownloadReport}
                        className="px-4 py-2 text-white bg-[#EB1C37] rounded-lg hover:bg-[#EB1C37]/90 flex items-center gap-2"
                        variant="default"
                        loading={downloadLoading}
                        loadingText="Generating..."
                        disabled={downloadLoading}
                      >
                        <Download className="h-4 w-4" />
                        Download Report
                      </Button>
                    )} */}
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Values are automatically updated based on the selected Fitment Readings specification
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                {/* Wall Thickness */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Wall Thickness ({getUnitLabel(unit)})
                  </Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500 font-medium mb-1 block">Min</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={getDisplayValue(toleranceSettings.toleranceWallThicknessMin)}
                        disabled
                        className="h-9 bg-gray-100 text-gray-900 cursor-not-allowed text-center"
                        placeholder="0.5"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500 font-medium mb-1 block">Max</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={getDisplayValue(toleranceSettings.toleranceWallThicknessMax)}
                        disabled
                        className="h-9 bg-gray-100 text-gray-900 cursor-not-allowed text-center"
                        placeholder="0.75"
                      />
                    </div>
                  </div>
                </div>

                {/* Land */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Land ({getUnitLabel(unit)})
                  </Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500 font-medium mb-1 block">Min</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={getDisplayValue(toleranceSettings.toleranceLandMin)}
                        disabled
                        className="h-9 bg-gray-100 text-gray-900 cursor-not-allowed text-center"
                        placeholder="1.0"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500 font-medium mb-1 block">Max</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={getDisplayValue(toleranceSettings.toleranceLandMax)}
                        disabled
                        className="h-9 bg-gray-100 text-gray-900 cursor-not-allowed text-center"
                        placeholder="2.0"
                      />
                    </div>
                  </div>
                </div>

                {/* Internal Hi-Lo */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Internal Hi-Lo ({getUnitLabel(unit)})
                  </Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500 font-medium mb-1 block">Min</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={getDisplayValue(toleranceSettings.toleranceInternalHiLoMin)}
                        disabled
                        className="h-9 bg-gray-100 text-gray-900 cursor-not-allowed text-center"
                        placeholder="0.5"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500 font-medium mb-1 block">Max</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={getDisplayValue(toleranceSettings.toleranceInternalHiLoMax)}
                        disabled
                        className="h-9 bg-gray-100 text-gray-900 cursor-not-allowed text-center"
                        placeholder="0.75"
                      />
                    </div>
                  </div>
                </div>

                {/* External Hi-Lo */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    External Hi-Lo ({getUnitLabel(unit)})
                  </Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500 font-medium mb-1 block">Min</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={getDisplayValue(toleranceSettings.toleranceExternalHiLoMin)}
                        disabled
                        className="h-9 bg-gray-100 text-gray-900 cursor-not-allowed text-center"
                        placeholder="0.5"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500 font-medium mb-1 block">Max</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={getDisplayValue(toleranceSettings.toleranceExternalHiLoMax)}
                        disabled
                        className="h-9 bg-gray-100 text-gray-900 cursor-not-allowed text-center"
                        placeholder="0.75"
                      />
                    </div>
                  </div>
                </div>

                {/* Bevel Top Range */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Bevel Top Range ({getUnitLabel(unit)})
                  </Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500 font-medium mb-1 block">Min</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={getDisplayValue(toleranceSettings.toleranceBevelTopRangeMin)}
                        disabled
                        className="h-9 bg-gray-100 text-gray-900 cursor-not-allowed text-center"
                        placeholder="0.5"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500 font-medium mb-1 block">Max</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={getDisplayValue(toleranceSettings.toleranceBevelTopRangeMax)}
                        disabled
                        className="h-9 bg-gray-100 text-gray-900 cursor-not-allowed text-center"
                        placeholder="0.75"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Data Table */}
      <Card>
        <CardContent className="p-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                    <TableRow>
                   <TableHead className="text-center w-16"></TableHead>
                   <TableHead className="text-center w-24" colSpan={3}>
                     <div className="text-center">
                       <div className="font-semibold text-sm">Land Edge ({getUnitLabel(unit)})</div>
                     </div>
                   </TableHead>
                   <TableHead className="text-center w-32"></TableHead>
                   <TableHead className="text-center bg-gray-100 w-32"></TableHead>
                   <TableHead className="text-center w-32" colSpan={2}>
                     <div className="text-center">
                       <div className="font-semibold text-sm">Wall Thickness ({getUnitLabel(unit)})</div>
                     </div>
                   </TableHead>
                   <TableHead className="text-center bg-gray-100 w-32"></TableHead>
                   <TableHead className="text-center w-32"></TableHead>
                   <TableHead className="text-center w-32"></TableHead>
                 </TableRow>
                <TableRow>
                  <TableHead className="text-center w-16">Ref Location</TableHead>
                  <TableHead className="text-center w-24">Pipe</TableHead>
                  <TableHead className="text-center w-24">Forging</TableHead>
                  <TableHead className="text-center w-24">Difference</TableHead>
                  <TableHead className="text-center w-32">Measured Land Hi/Lo ({getUnitLabel(unit)})</TableHead>
                  <TableHead className="text-center bg-gray-100 w-32">Calculated Internal Hi-Lo ({getUnitLabel(unit)})</TableHead>
                  <TableHead className="text-center w-32">Wall Thickness Pipe ({getUnitLabel(unit)})</TableHead>
                  <TableHead className="text-center w-32">Wall Thickness Forging ({getUnitLabel(unit)})</TableHead>
                  <TableHead className="text-center bg-gray-100 w-32">Calculated External Hi-Lo ({getUnitLabel(unit)})</TableHead>
                  <TableHead className="text-center w-32">Bevel Top Range ({getUnitLabel(unit)})</TableHead>
                  {/* <TableHead className="text-center w-32">action</TableHead> */}
                </TableRow>
              </TableHeader>
              <TableBody>
                {fitmentData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell className="text-center font-medium">{row.refLocation}</TableCell>
                    <TableCell className="text-center">
                      <Input
                        type="number"
                        step="0.01"
                        value={row.landEdgePipe === '' ? '' : getDisplayValue(typeof row.landEdgePipe === 'string' ? Number(row.landEdgePipe) : row.landEdgePipe)}
                        onChange={(e) => handleDataChange(index, 'landEdgePipe', e.target.value === '' ? '' : convertInputToInches(Number(e.target.value)))}
                        className={`h-8 text-sm text-center border-2 ${getToleranceBorderColor(
                          getDisplayValue(typeof row.landEdgePipe === 'string' ? Number(row.landEdgePipe) : row.landEdgePipe), 
                          getDisplayValue(toleranceSettings.toleranceLandMin), 
                          getDisplayValue(toleranceSettings.toleranceLandMax),
                          row.landEdgePipe
                        )}`}
                        placeholder="0"
                        disabled={!isEditMode}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Input
                        type="number"
                        step="0.01"
                        value={row.landEdgeForging === '' ? '' : getDisplayValue(typeof row.landEdgeForging === 'string' ? Number(row.landEdgeForging) : row.landEdgeForging)}
                        onChange={(e) => handleDataChange(index, 'landEdgeForging', e.target.value === '' ? '' : convertInputToInches(Number(e.target.value)))}
                        className={`h-8 text-sm text-center border-2 ${getToleranceBorderColor(
                          getDisplayValue(typeof row.landEdgeForging === 'string' ? Number(row.landEdgeForging) : row.landEdgeForging), 
                          getDisplayValue(toleranceSettings.toleranceLandMin), 
                          getDisplayValue(toleranceSettings.toleranceLandMax),
                          row.landEdgeForging
                        )}`}
                        placeholder="0"
                        disabled={!isEditMode}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="h-8 text-sm text-center border-2 rounded-md flex items-center justify-center bg-gray-50">
                        <span className="font-medium">{
                          (() => {
                            // If landEdgeDifference is 0 or empty, recalculate it
                            if (row.landEdgeDifference === 0 || row.landEdgeDifference === null || row.landEdgeDifference === undefined) {
                              const pipeValue = typeof row.landEdgePipe === 'string' ? (row.landEdgePipe === '' ? 0 : Number(row.landEdgePipe)) : (row.landEdgePipe || 0);
                              const forgingValue = typeof row.landEdgeForging === 'string' ? (row.landEdgeForging === '' ? 0 : Number(row.landEdgeForging)) : (row.landEdgeForging || 0);
                              const difference = pipeValue - forgingValue;
                              return getDisplayDifferenceValue(Math.round(difference * 1000) / 1000);
                            }
                            return getDisplayDifferenceValue(row.landEdgeDifference);
                          })()
                        }</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Input
                        type="number"
                        step="0.01"
                        value={row.measuredLandHiLo === '' ? '' : getDisplayValue(typeof row.measuredLandHiLo === 'string' ? Number(row.measuredLandHiLo) : row.measuredLandHiLo)}
                        onChange={(e) => handleDataChange(index, 'measuredLandHiLo', e.target.value === '' ? '' : convertInputToInches(Number(e.target.value)))}
                        className={`h-8 text-sm text-center border-2 ${getToleranceBorderColor(
                          getDisplayValue(typeof row.measuredLandHiLo === 'string' ? Number(row.measuredLandHiLo) : row.measuredLandHiLo), 
                          getDisplayValue(toleranceSettings.toleranceInternalHiLoMin), 
                          getDisplayValue(toleranceSettings.toleranceInternalHiLoMax),
                          row.measuredLandHiLo
                        )}`}
                        placeholder="0"
                        disabled={!isEditMode}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <div className={`h-8 text-sm text-center border-2 rounded-md flex items-center justify-center bg-gray-100 ${getToleranceBorderColor(
                        getDisplayValue(row.calculatedInternalHiLo), 
                        getDisplayValue(toleranceSettings.toleranceInternalHiLoMin), 
                        getDisplayValue(toleranceSettings.toleranceInternalHiLoMax),
                        row.calculatedInternalHiLo
                      )}`}>
                        <span className="font-medium">{
                          (() => {
                            // If calculatedInternalHiLo is 0 or empty, recalculate it
                            if (row.calculatedInternalHiLo === 0 || row.calculatedInternalHiLo === null || row.calculatedInternalHiLo === undefined) {
                              const safeToNumber = (val: any): number => {
                                if (val === '' || val === null || val === undefined) return 0;
                                const num = Number(val);
                                return isNaN(num) ? 0 : num;
                              };
                              const pipeValue = safeToNumber(row.landEdgePipe);
                              const forgingValue = safeToNumber(row.landEdgeForging);
                              const measuredValue = safeToNumber(row.measuredLandHiLo);
                              const difference = pipeValue - forgingValue;
                              const calculatedValue = difference + measuredValue;
                              return getDisplayDifferenceValue(Math.round(calculatedValue * 1000) / 1000);
                            }
                            return getDisplayDifferenceValue(row.calculatedInternalHiLo);
                          })()
                        }</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Input
                        type="number"
                        step="0.01"
                        value={row.wallThicknessPipe === '' ? '' : getDisplayValue(typeof row.wallThicknessPipe === 'string' ? Number(row.wallThicknessPipe) : row.wallThicknessPipe)}
                        onChange={(e) => handleDataChange(index, 'wallThicknessPipe', e.target.value === '' ? '' : convertInputToInches(Number(e.target.value)))}
                        className={`h-8 text-sm text-center border-2 ${getToleranceBorderColor(
                          getDisplayValue(typeof row.wallThicknessPipe === 'string' ? Number(row.wallThicknessPipe) : row.wallThicknessPipe), 
                          getDisplayValue(toleranceSettings.toleranceWallThicknessMin), 
                          getDisplayValue(toleranceSettings.toleranceWallThicknessMax),
                          row.wallThicknessPipe
                        )}`}
                        placeholder="0"
                        disabled={!isEditMode}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Input
                        type="number"
                        step="0.01"
                        value={row.wallThicknessForging === '' ? '' : getDisplayValue(typeof row.wallThicknessForging === 'string' ? Number(row.wallThicknessForging) : row.wallThicknessForging)}
                        onChange={(e) => handleDataChange(index, 'wallThicknessForging', e.target.value === '' ? '' : convertInputToInches(Number(e.target.value)))}
                        className={`h-8 text-sm text-center border-2 ${getToleranceBorderColor(
                          getDisplayValue(typeof row.wallThicknessForging === 'string' ? Number(row.wallThicknessForging) : row.wallThicknessForging), 
                          getDisplayValue(toleranceSettings.toleranceWallThicknessMin), 
                          getDisplayValue(toleranceSettings.toleranceWallThicknessMax),
                          row.wallThicknessForging
                        )}`}
                        placeholder="0"
                        disabled={!isEditMode}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <div className={`h-8 text-sm text-center border-2 rounded-md flex items-center justify-center bg-gray-100 ${getToleranceBorderColor(
                        getDisplayValue(row.calculatedExternalHiLo), 
                        getDisplayValue(toleranceSettings.toleranceExternalHiLoMin), 
                        getDisplayValue(toleranceSettings.toleranceExternalHiLoMax),
                        row.calculatedExternalHiLo
                      )}`}>
                        <span className="font-medium">{
                          (() => {
                            // If calculatedExternalHiLo is 0 or empty, recalculate it
                            if (row.calculatedExternalHiLo === 0 || row.calculatedExternalHiLo === null || row.calculatedExternalHiLo === undefined) {
                              const safeToNumber = (val: any): number => {
                                if (val === '' || val === null || val === undefined) return 0;
                                const num = Number(val);
                                return isNaN(num) ? 0 : num;
                              };
                              const pipeValue = safeToNumber(row.landEdgePipe);
                              const forgingValue = safeToNumber(row.landEdgeForging);
                              const measuredValue = safeToNumber(row.measuredLandHiLo);
                              const wallPipeValue = safeToNumber(row.wallThicknessPipe);
                              const wallForgingValue = safeToNumber(row.wallThicknessForging);
                              const difference = pipeValue - forgingValue;
                              const sum = difference + measuredValue;
                              const calculatedValue = -wallPipeValue + sum + wallForgingValue;
                              return getDisplayDifferenceValue(Math.round(calculatedValue * 1000) / 1000);
                            }
                            return getDisplayDifferenceValue(row.calculatedExternalHiLo);
                          })()
                        }</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Input
                        type="number"
                        step="0.01"
                        value={row.bevelTopRange === '' ? '' : getDisplayValue(typeof row.bevelTopRange === 'string' ? Number(row.bevelTopRange) : row.bevelTopRange)}
                        onChange={(e) => handleDataChange(index, 'bevelTopRange', e.target.value === '' ? '' : convertInputToInches(Number(e.target.value)))}
                        className={`h-8 text-sm text-center border-2 ${getToleranceBorderColor(
                          getDisplayValue(typeof row.bevelTopRange === 'string' ? Number(row.bevelTopRange) : row.bevelTopRange), 
                          getDisplayValue(toleranceSettings.toleranceBevelTopRangeMin), 
                          getDisplayValue(toleranceSettings.toleranceBevelTopRangeMax),
                          row.bevelTopRange
                        )}`}
                        placeholder="0"
                        disabled={!isEditMode}
                      />
                    </TableCell>
                    {/* <TableCell className="text-center w-32">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteRow(index)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete row"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell> */}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Total Readings: {fitmentData.length}
            </div>
            {/* <div className="flex gap-2">

              <Button onClick={addNewRow} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Row
              </Button>
            </div> */}
          </div>
        </CardContent>
      </Card>

      {/* Notes Section */}
      <Card>
        <CardContent className="p-6">
          <Label className="text-base font-medium">Notes:</Label>
          <textarea
            value={notes}
            onChange={(e) => handleNotesChange(e.target.value)}
            disabled={!isEditMode}
            className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[100px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:border-blue-700"
            placeholder="Enter any additional notes or observations..."
          />
        </CardContent>
      </Card>

      {/* Signature Section */}
      <Signature
        customerSignature={customerSignature}
        qcSignature={qcSignature}
        customerRepresentativeList={customerRepresentativeList}
        qcList={qcList}
        handleQcSignatureChange={handleQcSignatureChange}
        handleCustomerSignatureChange={handleCustomerSignatureChange}
        onSignatureChange={OnHandleSIngatuse}
        signatureData={fitmentWeldData}
        canSign={canSign}
        canEdit={canEdit}
        isEditMode={isEditMode}
        isCustomerRepresentativeUser={isCustomerRepresentativeUser}
        isQCInspectorUser={isQCInspectorUser}
        isWelderUser={isWelderUser}
        currentUserTeamMemberId={currentUserTeamMemberId}
        isAdminOrSuperAdmin={isAdminOrSuperAdmin}
      />
    </div>
  );
}; 